import React from "react";

import "./Pagination.less";

//---------------- interfaces --------------------
/**
 * Props for Bp2Pagination component.
 */
export interface PaginationProps {
   /**
    * Initial page number.
    */
   initialPage: number;
   /**
    * Default page size.
    */
   defaultPageSize: number;
   /**
    * Total number of items in list.
    */
   totalItems: number;
   /**
    * Additional class name for styling.
    */
   customClassName?: string;
   /**
    * Custom icon for pagination.
    */
   customIcon?: React.ReactNode;
   /**
    * Callback function triggered when page changes.
    * @param pageNumber - The new page number.
    */
   onChange: (pageNumber: number) => void;
}

//---------------- functions ------------------------

/**
 * Calculates the total number of pages based on the total number of items and the default page size.
 * @param {number} totalItems The total number of items.
 * @param {number} defaultPageSize The default page size.
 * @returns {number} The total number of pages.
 */
export const calculatePagesQuantity = (totalItems, defaultPageSize) => {
   return Math.ceil(totalItems / defaultPageSize);
};

/**
 * Checks if the right navigation buttons should be rendered based on the number of pages and the current range.
 * @param {number} pagesQuantity The total number of pages.
 * @param {number[]} currentRange The current range of pages being displayed.
 * @returns {boolean} A boolean value indicating whether the right navigation buttons should be rendered.
 */
export const checkIfRightButtonsShouldRender = (pagesQuantity, currentRange) => {
   if (pagesQuantity > 5 && !currentRange?.includes(pagesQuantity)) {
      return true;
   } else return false;
};

/**
 * Checks if the initial page exceeds the pages quantity and returns the appropriate value.
 * @param {number} initialPage The initial page number.
 * @param {number} pagesQuantity The total number of pages.
 * @returns {number} The corrected initial page number. If initialPage exceeds pagesQuantity, returns pagesQuantity, otherwise returns initialPage.
 */
export const checkInitialPageLimit = (initialPage: number, pagesQuantity: number) => {
   if (initialPage > pagesQuantity) {
      return pagesQuantity;
   } else return initialPage;
};

//--------------- layout functions ----------------------

/**
 * A function that is used to generate JSX code, which is then added to the list and rendered. Here we also handle clicking in page button.
 * @param {number} pageNumber - The page number displayed on the button.
 * @param {function} handlePageChange - setState function to override current page
 * @returns {JSX.Element} A JSX element representing a given page.
 */
export const createPageButton = (pageNumber, handlePageChange, currentPage) => {
   return (
      <button
         key={pageNumber}
         //  style={pageNumber === currentPage ? { backgroundColor: "lightblue" } : {}}
         onClick={() => handlePageChange(pageNumber)}
         className={
            pageNumber === currentPage
               ? "signle-page-button pagination-button-active"
               : "signle-page-button"
         }
      >
         {pageNumber}
      </button>
   );
};

//-------------------- Buttons behavior ------------------------

/**
 * Retrieves numbers smaller than the current page number.
 *
 * @param {number[]} list - The list of numbers.
 * @param {number} currentPage - The current page number.
 * @returns {number[]} - An array containing numbers smaller than the current page.
 */
const getSmallerNumbers = (list: number[], currentPage: number): number[] => {
   return list.filter((num) => num < currentPage && num >= currentPage - 2);
};

/**
 * Retrieves numbers greater than the current page number.
 *
 * @param {number[]} list - The list of numbers.
 * @param {number} currentPage - The current page number.
 * @returns {number[]} - An array containing numbers greater than the current page.
 */
const getBiggerNumbers = (list: number[], currentPage: number): number[] => {
   return list.filter((num) => num > currentPage && num <= currentPage + 2);
};

/**
 * Retrieves neighboring numbers around the current page number.
 *
 * @param {number[]} pages - The list of every pages.
 * @param {number} currentPage - The current page number.
 * @returns {number[]} - An sorted array containing neighboring numbers around the current page (and currentPage itself).
 */
const getNeighboringNumbers = (pages: number[], currentPage: number): number[] => {
   const smallerNumbers = getSmallerNumbers(pages, currentPage);
   const biggerNumbers = getBiggerNumbers(pages, currentPage);

   const neighboringPages = smallerNumbers.concat(biggerNumbers, currentPage).sort((a, b) => a - b);

   return neighboringPages;
};

/**
 * Function that returns the range of navigation buttons to display on the page. The range of displayed buttons is calculated based on the currentPage. To lern more inspect this function
 * @param {number} currentPage The current page.
 * @param {number[]} pages List of all available pages.
 * @returns {number[]} Range of navigation buttons to display.
 */
export const getButtonsRange = (currentPage, pages) => {
   // those if`s are to provide UX effect, and constant buttons lenght
   if (currentPage < 6 && pages.length <= 6) {
      return pages;
   } else if (currentPage < 6 && pages.length >= 6) {
      return pages.slice(0, 6);
   } else if (currentPage <= 6 && pages.length <= 6) {
      return pages;
   } else if (currentPage >= 6 && pages.length > 6 && !pages.slice(-5).includes(currentPage)) {
      return getNeighboringNumbers(pages, currentPage);
   } else if (pages.length > 6 && pages.slice(-6).includes(currentPage)) {
      return pages.slice(-6);
      // if we are on for example page 14, and we change items per page, so we no longer have 14 pages, on one component rerender this function return undefined. to prevent this, we return [1]
   } else return [1];
};
